import { useEffect, useState } from "react";

import { AppDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";
import { useTran } from "@/hooks";

import { onboardingService } from "../services";

const MeetingRequestDialog = ({ open, onOpenChange, user, onArranged }) => {
  const { t } = useTran();
  const { user: authUser } = useAuthStore();
  const targetUser = user || authUser || {};

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(t("Meeting request to complete project setup"));
      setMessage(
        t(
          "I'd like to schedule a consultation to discuss the project setup. Could you please share the time slots you're available over the next week to complete the project setup?",
        ),
      );
      setEmailErr("");
    }
  }, [open, t]);

  const isValidEmail = (value) => /.+@.+\..+/.test(String(value || "").trim());

  const submit = async () => {
    if (!isValidEmail(targetUser?.email)) {
      setEmailErr(t("Please enter a valid email address."));
      return;
    }

    setIsSubmitting(true);

    const defaultSubject = t("Meeting request to complete project setup");
    const defaultMessage = t(
      "I'd like to schedule a consultation to discuss the project setup. Could you please share the time slots you're available over the next week to complete the project setup?",
    );

    const payload = {
      recipientEmail: targetUser.email,
      recipientName: targetUser.username || targetUser.name || targetUser.email,
      subject: subject || defaultSubject,
      message: message || defaultMessage,
      userId: targetUser.id || targetUser._id || targetUser.userId,
    };

    try {
      const { success } = await onboardingService.sendMeetingRequest(payload, {
        showErrorToast: false,
      });
      if (!success) {
        throw new Error(t("Failed to send meeting request. Please try again."));
      }
      showToast.success(t("Meeting request sent successfully!"));
      onArranged?.({ email: targetUser.email, subject, message });
      onOpenChange?.(false);
    } catch (error) {
      showToast.error(
        error?.message ||
          t("Failed to send meeting request. Please try again."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("Request a meeting")}
      description={t(
        "Let the team know when you want to meet to complete onboarding.",
      )}
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={isSubmitting}
          >
            {t("Cancel")}
          </Button>
          <Button onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? t("Sending...") : t("Send")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t("To")}</Label>
          <div className="text-sm text-muted-foreground">
            {targetUser?.email || t("No email available")}
          </div>
          {emailErr && <p className="text-xs text-destructive">{emailErr}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="meeting-subject">{t("Subject")}</Label>
          <Input
            id="meeting-subject"
            type="text"
            placeholder={t("Meeting request to complete project setup")}
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="meeting-message">{t("Message")}</Label>
          <Textarea
            id="meeting-message"
            rows={8}
            placeholder={t("Hi, I would like to arrange a meeting...")}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
        </div>
      </div>
    </AppDialog>
  );
};

export default MeetingRequestDialog;
